import { useEffect } from 'react';
import { useTranslation } from './useTranslation';

// Hook para gerenciar meta tags dinâmicas
export const useMetaTags = () => {
  const { t, currentLanguage } = useTranslation();

  useEffect(() => {
    // Função para atualizar meta tags
    const updateMetaTags = () => {
      const title = t('meta.title');
      const description = t('meta.description');
      const keywords = t('meta.keywords');
      const noscript = t('meta.noscript');

      // Atualizar title
      document.title = title;

      // Atualizar meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      } else {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        metaDescription.setAttribute('content', description);
        document.head.appendChild(metaDescription);
      }

      // Atualizar meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', keywords);
      } else {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        metaKeywords.setAttribute('content', keywords);
        document.head.appendChild(metaKeywords);
      }

      // Atualizar meta language
      let metaLanguage = document.querySelector('meta[name="language"]');
      if (metaLanguage) {
        metaLanguage.setAttribute('content', currentLanguage);
      } else {
        metaLanguage = document.createElement('meta');
        metaLanguage.setAttribute('name', 'language');
        metaLanguage.setAttribute('content', currentLanguage);
        document.head.appendChild(metaLanguage);
      }

      // Atualizar Open Graph tags
      const updateOpenGraphTag = (property, content) => {
        let metaTag = document.querySelector(`meta[property="${property}"]`);
        if (metaTag) {
          metaTag.setAttribute('content', content);
        } else {
          metaTag = document.createElement('meta');
          metaTag.setAttribute('property', property);
          metaTag.setAttribute('content', content);
          document.head.appendChild(metaTag);
        }
      };

      // Atualizar Twitter tags
      const updateTwitterTag = (name, content) => {
        let metaTag = document.querySelector(`meta[name="${name}"]`);
        if (metaTag) {
          metaTag.setAttribute('content', content);
        } else {
          metaTag = document.createElement('meta');
          metaTag.setAttribute('name', name);
          metaTag.setAttribute('content', content);
          document.head.appendChild(metaTag);
        }
      };

      // Open Graph tags
      updateOpenGraphTag('og:title', title);
      updateOpenGraphTag('og:description', description);

      // Twitter tags
      updateTwitterTag('twitter:title', title);
      updateTwitterTag('twitter:description', description);

      // Atualizar noscript
      const noscriptElement = document.querySelector('noscript');
      if (noscriptElement) {
        noscriptElement.textContent = noscript;
      }

      // Atualizar JSON-LD estruturado
      updateStructuredData();

      // Atualizar manifest dinâmico
      updateManifest();
    };

    // Função para atualizar dados estruturados JSON-LD
    const updateStructuredData = () => {
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": t('structuredData.name'),
        "description": t('structuredData.description'),
        "applicationCategory": t('structuredData.applicationCategory'),
        "operatingSystem": t('structuredData.operatingSystem'),
        "url": t('structuredData.url'),
        "offers": {
          "@type": "Offer",
          "price": t('structuredData.offers.price'),
          "priceCurrency": t('structuredData.offers.priceCurrency')
        },
        "author": {
          "@type": "Organization",
          "name": t('structuredData.author.name')
        },
        "publisher": {
          "@type": "Organization",
          "name": t('structuredData.publisher.name'),
          "url": t('structuredData.publisher.url')
        },
        "datePublished": "2025-01-22",
        "dateModified": "2025-01-22"
      };

      // Remover JSON-LD existente
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Adicionar novo JSON-LD
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    };

    // Função para atualizar manifest dinâmico
    const updateManifest = () => {
      const manifestData = {
        "short_name": t('manifest.short_name'),
        "name": t('manifest.name'),
        "description": t('manifest.description'),
        "icons": [
          {
            "src": "favicon.png",
            "sizes": "192x192",
            "type": "image/png"
          },
          {
            "src": "favicon.png",
            "sizes": "512x512",
            "type": "image/png"
          }
        ],
        "start_url": ".",
        "display": "standalone",
        "theme_color": "#4CAF50",
        "background_color": "#FFFFFF"
      };

      // Atualizar link do manifest
      let manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) {
        // Criar um novo link com timestamp para forçar atualização
        const newLink = document.createElement('link');
        newLink.rel = 'manifest';
        newLink.href = `data:application/json,${encodeURIComponent(JSON.stringify(manifestData))}`;
        
        // Remover link antigo e adicionar novo
        manifestLink.remove();
        document.head.appendChild(newLink);
      }
    };

    // Atualizar meta tags quando o idioma muda
    updateMetaTags();
  }, [t, currentLanguage]);

  return {
    updateMetaTags: () => {
      const title = t('meta.title');
      const description = t('meta.description');
      const keywords = t('meta.keywords');
      
      document.title = title;
      
      // Atualizar meta tags existentes
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) metaDescription.setAttribute('content', description);
      
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) metaKeywords.setAttribute('content', keywords);
    }
  };
};
